import React from "react";
import {
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Box,
  ListSubheader,
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";
import styles from "./CustomDropdown.module.scss";
import KeyboardArrowDown from "../icon-components/KeyboardArrowDown";

interface CustomDropdownProps {
  placeholder?: string;
  inputProps?: object;
  inputFormTitle?: string;
  value: string | number;
  isRequired?: boolean;
  changeTrigger: (value: string | number) => void;
  disabled?: boolean;
  error?: boolean;
  options: Array<{ label: string; value: string | number }>;
  groupedOptions?: Array<{
    group: string;
    options: Array<{ label: string; value: number }>;
  }>;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  placeholder = "",
  inputProps = {},
  inputFormTitle = "",
  value,
  isRequired = false,
  changeTrigger,
  disabled = false,
  error = false,
  options = [],
  groupedOptions = [],
}) => {
  const handleChange = (event: SelectChangeEvent<string | number>) => {
    changeTrigger(event.target.value as string | number);
  };

  const getDisplayValue = (value: number | string) => {
    if (groupedOptions.length > 0) {
      for (const group of groupedOptions) {
        for (const option of group.options) {
          if (option.value === value) {
            return `${group.group ? group.group + " - " : ""}${option.label}`;
          }
        }
      }
    } else {
      const option = options.find((option) => option.value === value);
      return option ? option.label : placeholder;
    }
    return placeholder;
  };

  interface GroupedOption {
    group: string;
    options: Array<{ label: string; value: number }>;
  }

  const renderSelectGroup = (group: GroupedOption): React.ReactNode[] => {
    const items = group.options.map((option) => (
      <MenuItem
        key={option.value}
        value={option.value}
        className={styles.dropdownItem}
      >
        {option.label}
      </MenuItem>
    ));
    return [
      <ListSubheader key={group.group} className={styles.dropdownSubheader}>
        {group.group}
      </ListSubheader>,
      ...items,
    ];
  };

  const menuProps: SelectProps["MenuProps"] = {
    PaperProps: {
      sx: {
        width: "300px",
        maxWidth: "100%",
        ".MuiMenu-list": {
          padding: "0",
        },
        "& .MuiMenuItem-root": {
          borderBottom: "1px solid #bdbdbd",
          "&:last-child": {
            borderBottom: "none",
          },
        },
        "& .MuiListSubheader-root": {
          backgroundColor: "#F2EEE8",
          color: "#000000",
          fontSize: "14px",
          fontWeight: "600",
          lineHeight: "34px",
        },
        "& .dropdown-item": {
          paddingLeft: "2rem",
          fontSize: "14px",
        },
        "& .Mui-selected": {
          backgroundColor: "#f5f5f5 !important",
        },
        "& .MuiSelect-select": {
          padding: "8px",
          lineHeight: "24px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis", // 💡 Adds ellipsis for long text
        },
      },
    },
  };

  return (
    <div className={styles.customDropdown}>
      <FormControl sx={{ flex: 1, width: "100%" }}>
        <Box className={styles.labelArea}>
          <FormLabel
            htmlFor="dropdown"
            className={styles.dropdownTitle}
            sx={{
              "&.Mui-focused": {
                color: "inherit",
              },
            }}
          >
            {inputFormTitle}{" "}
            {isRequired && <span className={styles.requiredAsterisk}>*</span>}
          </FormLabel>
        </Box>
        <Select
          className={styles.dropdownInput}
          value={value}
          onChange={handleChange}
          displayEmpty
          inputProps={{ ...inputProps, "aria-labelledby": "dropdown-label" }}
          disabled={disabled}
          error={error}
          fullWidth
          renderValue={(selected) =>
            getDisplayValue(selected as string | number)
          }
          MenuProps={menuProps}
          IconComponent={KeyboardArrowDown}
          sx={{
            flex: 1,
            "label+&": {
              marginTop: "0px",
            },
            "&:before": {
              borderBottom: "none",
            },
            "&:after": {
              borderBottom: "none",
            },
            "&:hover:not(.Mui-disabled, .Mui-error):before": {
              borderBottom: "none",
            },
            ".MuiSelect-select": {
              padding: "8px",
              lineHeight: "24px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#bdbdbd",
            },
            "& svg": {
              padding: "5px",
            },
            "&.Mui-error": {
              border: "1px solid #D50032",
              "&.Mui-focused": {
                border: "2px solid #D50032",
              },
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#bdbdbd",
              borderWidth: "1px",
            },
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: "#bdbdbd",
            },
            "&.Mui-disabled": {
              cursor: "not-allowed",
              ".MuiSelect-select": {
                cursor: "not-allowed",
              },
              ".MuiSelect-icon": {
                cursor: "not-allowed",
              },
            },
            "& .MuiSelect-icon": {
              color: "#000000",
              fontSize: "2rem",
            },
          }}
          data-testid="modal-dropdown"
        >
          <MenuItem disabled value={0}>
            {placeholder}
          </MenuItem>

          {groupedOptions.length > 0
            ? groupedOptions.map((group) => renderSelectGroup(group))
            : options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  className={styles.dropdownItem}
                >
                  {option.label}
                </MenuItem>
              ))}
        </Select>
      </FormControl>
    </div>
  );
};

export default CustomDropdown;
